"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Factory, Building, FolderKanban, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardTitle, CardDescription } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface ServiceItem {
  id: string;
  title: string;
  category: string;
  description: string;
  to: string;
  iconName: "commercial" | "industrial" | "facilities" | "projects";
  features: string;
  photoName: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const servicesData: ServiceItem[] = [{
  id: "commercial-scaffolding",
  title: "Commercial scaffolding",
  category: "Main Contractors & Developments",
  description: "Engineered access frameworks designed for new builds, refurbishments, and high-footfall sites with minimal disruption.",
  to: "/CommercialScaffoldingPage",
  iconName: "commercial",
  features: "TG20:21 compliant, daily inspections, phased handovers",
  photoName: "Refurbishment Related Commercial Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_hero.png-wvc-sizes",
  imageAlt: "Commercial scaffolding erected on modern office building"
}, {
  id: "industrial-scaffolding",
  title: "Industrial scaffolding",
  category: "Plant, Utility & Manufacturing",
  description: "Heavy-duty access and support structures suited for plants, utilities, shutdowns, and complex industrial maintenance.",
  to: "/IndustrialScaffoldingPage",
  iconName: "industrial",
  features: "Specialist load ratings, confined space certified, safety protocols",
  photoName: "Refurbishment Related Industrial Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/industrial_scaffolding_access.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/industrial_scaffolding_access.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/industrial_scaffolding_access.png-wvc-sizes",
  imageAlt: "Industrial scaffolding structure around manufacturing facility"
}, {
  id: "property-facilities",
  title: "Property & facilities management",
  category: "Estates & Maintenance",
  description: "Planned and rapid-response access solutions for estates, social housing portfolios, and commercial facilities teams.",
  to: "/PropertyFacilitiesManagementPage",
  iconName: "facilities",
  features: "Tenant-safe hoarding, programmed works, South East coverage",
  photoName: "Refurbishment Related Facilities Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/facilities_scaffolding_maintenance.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/facilities_scaffolding_maintenance.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/facilities_scaffolding_maintenance.png-wvc-sizes",
  imageAlt: "Scaffolding for property maintenance and external repairs"
}, {
  id: "case-studies-projects",
  title: "Projects & case studies",
  category: "Track Record & Gallery",
  description: "Review our recent access deliveries across Berkshire, Surrey, Hampshire, Buckinghamshire, and Greater London.",
  to: "/ProjectsCaseStudiesPage",
  iconName: "projects",
  features: "Verified site photos, technical briefs, client outcomes",
  photoName: "Refurbishment Related Projects Image",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/projects_scaffolding_portfolio.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/projects_scaffolding_portfolio.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/projects_scaffolding_portfolio.png-wvc-sizes",
  imageAlt: "Portfolio of completed scaffolding projects across the South East"
}];
function getResolvedImage(posts: any[], photoName: string, fallback: {
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
}) {
  const matched = posts?.find(p => p.title?.toLowerCase() === photoName.toLowerCase() || p.customFields?.photo_name?.toLowerCase() === photoName.toLowerCase());
  if (matched) {
    const customImg = matched.customFields?.image || matched.featuredImage;
    if (customImg) {
      return {
        src: customImg,
        srcset: "",
        sizes: "",
        alt: matched.customFields?.alt_text || matched.title || fallback.alt
      };
    }
  }
  return fallback;
}
function RelatedServicesContent() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (name: ServiceItem["iconName"]) => {
    switch (name) {
      case "commercial":
        return <Building2 className="h-5 w-5 text-primary" />;
      case "industrial":
        return <Factory className="h-5 w-5 text-primary" />;
      case "facilities":
        return <Building className="h-5 w-5 text-primary" />;
      case "projects":
        return <FolderKanban className="h-5 w-5 text-primary" />;
      default:
        return <Building2 className="h-5 w-5 text-primary" />;
    }
  };
  return <section id="relatedservices" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12">
          <div className="max-w-2xl space-y-3">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5">
              Access Capabilities
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Explore our scaffolding services
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              Find the right access setup for your commercial, industrial, or facilities project
              across Berkshire, Surrey, Hampshire, and London.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button asChild size="lg" className="shadow-sm">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.map((service, i) => {
          const resolvedImg = getResolvedImage(posts, service.photoName, {
            src: service.imageSrc,
            srcset: service.imageSrcset,
            sizes: service.imageSizes,
            alt: service.imageAlt
          });
          return <div key={service.id} data-index={i} className="flex">
                <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.15
            }} transition={{
              duration: 0.45,
              delay: i * 0.08
            }} className="w-full flex">
                  <Card className="group relative flex flex-col justify-between w-full overflow-hidden border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/40">
                    {/* Image Header */}
                    <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                      {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={resolvedImg.src} data-wvc-srcset={resolvedImg.srcset} data-wvc-sizes={resolvedImg.sizes} alt={resolvedImg.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center group-hover:scale-102 transition-transform duration-300" />}
                      <div className="absolute top-3 left-3 bg-card/95 backdrop-blur-xs rounded-md p-2 shadow-2xs border border-border/50">
                        {getServiceIcon(service.iconName)}
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="flex flex-col flex-1 justify-between p-6">
                      <div className="space-y-3">
                        <div>
                          <span className="text-xs font-medium text-muted-foreground">
                            {service.category}
                          </span>
                          <CardTitle className="text-lg font-bold text-foreground group-hover:text-primary transition-colors mt-1">
                            {service.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                          {service.description}
                        </CardDescription>
                      </div>

                      <div className="pt-5 mt-5 border-t border-border/70 space-y-4">
                        <div className="flex items-start gap-2 text-xs text-foreground/85">
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                          <span className="leading-tight">{service.features}</span>
                        </div>

                        <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:bg-primary/5 hover:text-primary group/btn">
                          <Link to={service.to}>
                            <span>View details</span>
                            <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </div>;
        })}
        </div>

        {/* Accreditations Trust Strip */}
        <div className="mt-12 rounded-xl border border-border bg-card p-6 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-primary/10 p-2 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Safety-led & fully accredited delivery
              </p>
              <p className="text-xs text-muted-foreground">
                Confirmed accreditations with NASC and CHAS.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <Button asChild variant="outline" size="sm">
              <Link to="/HealthSafetyAccreditationsPage">
                View accreditations
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
export default function RelatedServices() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 20
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 20,
    status: ["publish"]
  }}>
      <RelatedServicesContent />
    </PostsProvider>;
}